import { useEffect, useState } from 'react'

import { FiAlertTriangle, FiArrowRight } from 'react-icons/fi'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { useSettings } from '@loadsmart/react-frontend-settings'

import opendockIcon from 'assets/images/opendock_icon.png'
import shipperguideIcon from 'assets/images/shipperguide_icon.png'
import { type AuthErrorLocationState } from 'auth/domain/AuthError'
import { LocalStorageClient } from 'core/infra/storage/LocalStorageClient'

import { LoadsmartWordmark, OpendockWordmark, ShipperGuideWordmark } from './components/brandMarks'
import SignInHelpDialog from './components/SignInHelpDialog'
import {
  StyledAppCard,
  StyledAppDescription,
  StyledAppList,
  StyledAppName,
  StyledAuthError,
  StyledAuthErrorBody,
  StyledAuthErrorIcon,
  StyledAuthErrorTitle,
  StyledAuthHeading,
  StyledAuthInner,
  StyledAuthPanel,
  StyledBasicRow,
  StyledBasicSubtitle,
  StyledBasicTitle,
  StyledBrandCopy,
  StyledBrandHeadline,
  StyledBrandPanel,
  StyledBrandRail,
  StyledDemoCard,
  StyledDemoCta,
  StyledDemoSubtitle,
  StyledDemoTitle,
  StyledEyebrow,
  StyledFooter,
  StyledFooterDot,
  StyledGoIcon,
  StyledLinkButton,
  StyledLogo,
  StyledShell,
  StyledSplit,
  StyledTile,
} from './HomePage.styles'

const storage = new LocalStorageClient()

const ProductTile = ({ src }: { src: string }) => (
  <StyledTile aria-hidden="true">
    <img src={src} alt="" />
  </StyledTile>
)

const HomePage = () => {
  const { isLoading, values } = useSettings([
    'flags.ENABLE_LOGIN_THROUGH_BIP',
    'flags.ENABLE_LOGIN_THROUGH_OPENDOCK',
  ])
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isHelpOpen, setIsHelpOpen] = useState(false)

  // Set when a sign-in flow bounces the user back here, so the page can say why.
  const { authError } = (useLocation().state ?? {}) as AuthErrorLocationState

  const [isLoginThroughBIPEnabled, isOpenDockLoginEnabled] = values

  const isShipperGuideEnabled = Boolean(isLoginThroughBIPEnabled)
  const isOpendockEnabled = Boolean(isOpenDockLoginEnabled)

  // With the BIP flag on, /login is the ShipperGuide handoff and the standalone
  // form moves to /login-lmt. With it off there is only one form, at /login.
  const basicAuthPath = isShipperGuideEnabled ? '/login-lmt' : '/login'

  const hasProductSignIn = isShipperGuideEnabled || isOpendockEnabled

  useEffect(() => {
    const userAgent = window.navigator.userAgent
    const isMobile = /Mobi/i.test(userAgent)

    if (isMobile) {
      navigate('/mobile-not-supported')
    }

    // Don't clear localStorage if this is an OpenDock redirect
    // The OpenDock auth handler will manage tokens in this case
    const opendockAuth = searchParams.get('opendock_auth')
    const isOpendockAuth = opendockAuth && opendockAuth !== 'true' // Check if it's a real token, not just "true"

    if (!isOpendockAuth) {
      storage.clear()
    }
  }, [navigate, searchParams])

  return isLoading ? null : (
    <>
      <StyledShell>
        <StyledBrandPanel>
          <StyledBrandRail aria-hidden="true" />

          <StyledLogo>
            <LoadsmartWordmark />
          </StyledLogo>

          <StyledBrandCopy>
            <StyledEyebrow>Analytics Portal</StyledEyebrow>
            <StyledBrandHeadline>Take your data analysis to the next level</StyledBrandHeadline>
          </StyledBrandCopy>
        </StyledBrandPanel>

        <StyledAuthPanel>
          <StyledAuthInner>
            <StyledAuthHeading>Sign in</StyledAuthHeading>

            {authError && (
              <StyledAuthError role="alert">
                <StyledAuthErrorIcon aria-hidden="true">
                  <FiAlertTriangle />
                </StyledAuthErrorIcon>
                <StyledAuthErrorTitle>We couldn&rsquo;t sign you in</StyledAuthErrorTitle>
                <StyledAuthErrorBody>{authError.detail}</StyledAuthErrorBody>
              </StyledAuthError>
            )}

            <StyledAppList>
              {isShipperGuideEnabled && (
                <StyledAppCard to="/login" $delay="0.21s">
                  <ProductTile src={shipperguideIcon} />
                  <span>
                    <StyledAppName>
                      <ShipperGuideWordmark />
                    </StyledAppName>
                    <StyledAppDescription>See where your freight spend goes</StyledAppDescription>
                  </span>
                  <StyledGoIcon aria-hidden="true">
                    <FiArrowRight />
                  </StyledGoIcon>
                </StyledAppCard>
              )}

              {isOpendockEnabled && (
                <StyledAppCard to="/login-opendock" $delay="0.29s">
                  <ProductTile src={opendockIcon} />
                  <span>
                    <StyledAppName>
                      <OpendockWordmark />
                    </StyledAppName>
                    <StyledAppDescription>See how your docks really perform</StyledAppDescription>
                  </span>
                  <StyledGoIcon aria-hidden="true">
                    <FiArrowRight />
                  </StyledGoIcon>
                </StyledAppCard>
              )}
            </StyledAppList>

            {hasProductSignIn && <StyledSplit>Or</StyledSplit>}

            <StyledBasicRow to={basicAuthPath}>
              <span>
                <StyledBasicTitle>Email and password</StyledBasicTitle>
                <StyledBasicSubtitle>Standalone portal accounts</StyledBasicSubtitle>
              </span>
              <StyledGoIcon aria-hidden="true">
                <FiArrowRight />
              </StyledGoIcon>
            </StyledBasicRow>

            <StyledDemoCard to="/demo">
              <span>
                <StyledDemoTitle>Not a customer? See a demo</StyledDemoTitle>
                <StyledDemoSubtitle>
                  The whole portal, running on sample freight data. No account needed.
                </StyledDemoSubtitle>
              </span>
              <StyledDemoCta>
                Explore the demo
                <FiArrowRight aria-hidden="true" />
              </StyledDemoCta>
            </StyledDemoCard>

            <StyledFooter>
              <span>&copy; {new Date().getFullYear()} Loadsmart, Inc.</span>
              <StyledFooterDot aria-hidden="true" />
              <StyledLinkButton type="button" onClick={() => setIsHelpOpen(true)}>
                Get help
              </StyledLinkButton>
            </StyledFooter>
          </StyledAuthInner>
        </StyledAuthPanel>
      </StyledShell>

      <SignInHelpDialog
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        isShipperGuideEnabled={isShipperGuideEnabled}
        isOpendockEnabled={isOpendockEnabled}
      />
    </>
  )
}

export default HomePage
